import { useRef, useState, type ReactNode } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';

interface HoverDropdownProps {
  label: string;
  children: ReactNode;
  contentClassName?: string;
}

/**
 * Desktop nav dropdown that opens on hover (and still works on click/keyboard).
 * A short close delay keeps the menu open while the pointer travels from the
 * trigger to the portaled content.
 */
const HoverDropdown = ({ label, children, contentClassName }: HoverDropdownProps) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const openNow = () => {
    cancelClose();
    setOpen(true);
  };

  const closeSoon = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 140);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger
        onMouseEnter={openNow}
        onMouseLeave={closeSoon}
        onFocus={openNow}
        className="flex items-center text-muted-foreground hover:text-foreground transition-colors duration-200 text-sm font-medium focus:outline-none focus-visible:ring-1 focus-visible:ring-ring/50 focus-visible:ring-offset-0"
      >
        {label}
        <ChevronDown
          className={`ml-1 h-3 w-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={8}
        onMouseEnter={openNow}
        onMouseLeave={closeSoon}
        onCloseAutoFocus={(e) => e.preventDefault()}
        className={contentClassName}
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default HoverDropdown;
